import { useEffect, useState } from 'react';
import { Button, Container, Grid, Paper, Radio, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../components';
import { request, toast } from '../../../../../utils';
import { LIMIT_TYPE } from '../../../../../constants';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';

const SERVICE_HEADER = [
  {
    header: 'Type',
    accessor: 'type',
    sx: { textTransform: 'capitalize' }
  },
  {
    header: '',
    accessor: '',
    sx: { textTransform: 'capitalize' }
  }
];

const TEST_PRICE_MANAGEMENT_HEADER = [
  {
    header: 'Sp. Ser. Code',
    accessor: 'sp_ser_code',
    sx: { textTransform: 'capitalize' }
  },

  {
    header: 'Description',
    accessor: 'description'
  },
  {
    header: 'Opd Charge',
    accessor: 'opd_charge'
  },
  {
    header: 'Ipd Charge',
    accessor: 'ipd_charge'
  },
  {
    header: 'NABH OPd',
    accessor: 'nabh_opd'
  },
  {
    header: 'NABH Ipd',
    accessor: 'nabh_ipd'
  },
  {
    header: 'Def Discount',
    accessor: 'def_discount'
  }
];

export const TestPriceManagement = () => {
  const [serviceList, setServiceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        discount_authority: Yup.string().required('Discount Authority Is Required ')
      })
    ),
    mode: 'all'
  });
  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (selectedRecord) {
      setValue('discount_authority', selectedRecord.discount_authority);
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/' : '/', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        discount_authority: formValues.discount_authority,
        limit_type: formValues.limit_type
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Discount Authority master`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Discount Authority master`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Discount Authority master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Discount Authority master', { variant: 'error' });
    }
    setLoading(false);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3">
                Test Price Managment
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={"Submit"}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={handleSubmit(formSubmit)}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={"Cancel"}
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Read`} variant="contained" disabled={['new', 'edit'].includes(currentAction)}>
                  Query
                </CustomButtonn>
                <CustomButtonn id={`${lastPathSegment}Print`} variant="contained" disabled={!selectedRecord || currentAction}>
                  Print
                </CustomButtonn>
                <CustomButtonn id={"Exit"} variant="contained">Exit</CustomButtonn>
              </Stack>
            </Stack>

            <Grid container spacing={1}>
              <Grid item xs={6} sx={{ display: 'flex', minHeight: '30rem', flexDirection: 'column', gap: 0.3 }}>
                <Table
                  containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                  data={serviceList}
                  columns={SERVICE_HEADER}
                  size="medium"
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Service Name </Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.5 }}
                    name="service_name"
                  />
                  <Radio
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.2 }}
                    name="price_list"
                  />
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Price List</Typography>
                  <Radio
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.2 }}
                    name="bed_category_wise"
                  />
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Bed Category Wise</Typography>
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700 }}>Price List</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.6 }}
                    name="price_list"
                  />
                  <Button sx={{ flex: 0.2 }} variant="contained">
                    {' '}
                    View Data
                  </Button>
                </Stack>
                <Grid item xs={12} sx={{ display: 'flex', minHeight: '40rem', flexDirection: 'column', gap: 0.3 }}>
                  <Table
                    containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                    data={serviceList}
                    columns={TEST_PRICE_MANAGEMENT_HEADER}
                    size="medium"
                  />
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
